import { useMemo } from "react";
import { applicableSteps, weightOf } from "../steps/registry";

/**
 * Progress through onboarding, derived — never stored.
 *
 * ## The denominator is fixed from the first screen
 *
 * Counted over the steps this journey *numbers* (`applicableSteps`), not the
 * steps it can currently enter. Using reachability meant the total grew every
 * time the member answered something: the bar opened at "1 of 2" and stretched
 * out as they went, which reads as the finish line running away from them.
 *
 * ## Steps, not screens, for the total
 *
 * A goal sub-flow presents a different number of screens depending on how many
 * goals were chosen, so a screen-based total cannot be known until goals are
 * picked — and would visibly jump backwards at that moment, because the
 * numerator stays put while the denominator grows.
 *
 * So the total counts steps, and a multi-screen step contributes a *fraction*
 * as the member moves through it. The bar still advances on every screen inside
 * a sub-flow, which is what ECH-114 requires, and the total never moves.
 *
 * @param {string} currentStepId
 * @param {object} state          flow state
 * @param {number} [screensDone]  screens completed inside the current step
 * @returns {{fraction: number, stepNumber: number, stepTotal: number}}
 */
export const useProgress = (currentStepId, state, screensDone = 0) =>
  useMemo(() => {
    const steps = applicableSteps(state);
    const stepTotal = steps.length;

    const index = steps.findIndex((s) => s.id === currentStepId);

    // A step outside this journey contributes nothing rather than throwing the
    // fraction off — typically the flow was reset while a later screen was
    // still mounted.
    if (index === -1) {
      return { fraction: 0, stepNumber: 0, stepTotal };
    }

    // How far through the current step the member is, as a fraction of it. The
    // screen in hand counts: the bar and the caption are two readings of one
    // position, so "8 of 8" beside a bar with a step's worth of gap still in it
    // reads as a ninth step nobody can reach.
    const screensInStep = weightOf(steps[index], state);
    const screensCounted = Math.min(
      Math.max(screensDone, 0) + 1,
      screensInStep,
    );
    const within = screensInStep > 0 ? screensCounted / screensInStep : 0;

    const fraction = stepTotal === 0 ? 0 : (index + within) / stepTotal;

    return {
      fraction: Math.min(Math.max(fraction, 0), 1),
      stepNumber: index + 1,
      stepTotal,
    };
  }, [currentStepId, state, screensDone]);
